import type { ExerciseDef } from './types'

// ─── 10.1 Learning from preferences ───────────────────────────────────────────

export const rewardModel: ExerciseDef = {
  starter: `// Each answer is described by features: [correct, informative, friendly, rude]
const answers = [
  [1, 0.8, 0.9, 0],    // 0: great answer
  [1, 0.1, 0.2, 0.9],  // 1: right but rude
  [0, 0.6, 0.9, 0],    // 2: friendly but wrong
  [1, 0.4, 0.5, 0],    // 3: fine
]
// People's comparisons: [winner, loser]
const prefs: [number, number][] = [[0, 1], [0, 2], [3, 1], [3, 2], [0, 3], [2, 1]]

// A reward model scores an answer: r = w · features.
// Bradley–Terry: P(winner beats loser) = σ(r_winner − r_loser).
// Train w to maximise the log of that probability, i.e. minimise
//   loss = −ln σ(r_w − r_l)   whose gradient for w is   −(1 − σ(r_w − r_l)) · (f_w − f_l)
const sigmoid = (z: number) => 1 / (1 + Math.exp(-z))

function trainRewardModel(epochs: number, lr: number): number[] {
  let w = [0, 0, 0, 0]
  // for each epoch, for each preference pair: compute p = σ(r_w − r_l), then
  //   w ← w + lr · (1 − p) · (f_w − f_l)
  return w
}

const w = trainRewardModel(200, 0.5)
console.log("learned weights:", w.map(v => round(v, 2)))
console.log("rewards:", answers.map(f => round(vec.dot(w, f), 2)))
`,
  solution: `const answers = [
  [1, 0.8, 0.9, 0],
  [1, 0.1, 0.2, 0.9],
  [0, 0.6, 0.9, 0],
  [1, 0.4, 0.5, 0],
]
const prefs: [number, number][] = [[0, 1], [0, 2], [3, 1], [3, 2], [0, 3], [2, 1]]
const sigmoid = (z: number) => 1 / (1 + Math.exp(-z))

function trainRewardModel(epochs: number, lr: number): number[] {
  let w = [0, 0, 0, 0]
  for (let e = 0; e < epochs; e++) {
    for (const [win, lose] of prefs) {
      const p = sigmoid(vec.dot(w, answers[win]) - vec.dot(w, answers[lose]))
      const diff = vec.sub(answers[win], answers[lose])
      w = vec.add(w, vec.scale(diff, lr * (1 - p)))
    }
  }
  return w
}

const w = trainRewardModel(200, 0.5)
console.log("learned weights:", w.map(v => round(v, 2)))
console.log("rewards:", answers.map(f => round(vec.dot(w, f), 2)))
`,
  tests: `
const __r = trainRewardModel(200, 0.5)
const __score = i => vec.dot(__r, answers[i])
test("the great answer gets the highest reward", () => expect([0, 1, 2, 3].every(i => i === 0 || __score(0) > __score(i)), "answer 0 ranks first").toBeTruthy())
test("every human preference is respected", () => expect(prefs.every(([a, b]) => __score(a) > __score(b)), "all pairs ordered").toBeTruthy())
test("rudeness gets a negative weight", () => expect(__r[3], "weight on 'rude'").toBeLessThan(0))
`,
}

// ─── 10.2 Reinforcement learning ──────────────────────────────────────────────

export const qLearning: ExerciseDef = {
  starter: `// A corridor of 7 squares. Start in the middle (3).
// Reaching square 6 gives +1, square 0 gives −1; both end the episode.
// Actions: 0 = left, 1 = right.
const N = 7, START = 3
function stepEnv(s: number, a: number): { s2: number; reward: number; done: boolean } {
  const s2 = a === 1 ? s + 1 : s - 1
  if (s2 === N - 1) return { s2, reward: 1, done: true }
  if (s2 === 0) return { s2, reward: -1, done: true }
  return { s2, reward: 0, done: false }
}

// Q[s][a] = how good it is to take action a in state s.
// The Q-learning update:
//   target = reward                        if done
//          = reward + γ · max(Q[s2])       otherwise
//   Q[s][a] ← Q[s][a] + α · (target − Q[s][a])
function qUpdate(Q: number[][], s: number, a: number, reward: number, s2: number, done: boolean, alpha: number, gamma: number): void {
}

function train(episodes: number): number[][] {
  const Q = mat.zeros(N, 2)
  for (let ep = 0; ep < episodes; ep++) {
    let s = START
    for (let t = 0; t < 50; t++) {
      // ε-greedy: explore 20% of the time, otherwise take the best known action
      const a = random.random() < 0.2 ? random.int(0, 2) : vec.argmax(Q[s])
      const { s2, reward, done } = stepEnv(s, a)
      qUpdate(Q, s, a, reward, s2, done, 0.5, 0.9)
      s = s2
      if (done) break
    }
  }
  return Q
}

const Q = train(300)
console.log(Q.map((q, s) => \`square \${s}: left \${q[0].toFixed(2)}  right \${q[1].toFixed(2)}\`).join("\\n"))
`,
  solution: `const N = 7, START = 3
function stepEnv(s: number, a: number): { s2: number; reward: number; done: boolean } {
  const s2 = a === 1 ? s + 1 : s - 1
  if (s2 === N - 1) return { s2, reward: 1, done: true }
  if (s2 === 0) return { s2, reward: -1, done: true }
  return { s2, reward: 0, done: false }
}

function qUpdate(Q: number[][], s: number, a: number, reward: number, s2: number, done: boolean, alpha: number, gamma: number): void {
  const target = done ? reward : reward + gamma * Math.max(...Q[s2])
  Q[s][a] += alpha * (target - Q[s][a])
}

function train(episodes: number): number[][] {
  const Q = mat.zeros(N, 2)
  for (let ep = 0; ep < episodes; ep++) {
    let s = START
    for (let t = 0; t < 50; t++) {
      const a = random.random() < 0.2 ? random.int(0, 2) : vec.argmax(Q[s])
      const { s2, reward, done } = stepEnv(s, a)
      qUpdate(Q, s, a, reward, s2, done, 0.5, 0.9)
      s = s2
      if (done) break
    }
  }
  return Q
}

const Q = train(300)
console.log(Q.map((q, s) => \`square \${s}: left \${q[0].toFixed(2)}  right \${q[1].toFixed(2)}\`).join("\\n"))
`,
  tests: `
test("a terminal update moves Q toward the reward", () => {
  const __Q = mat.zeros(3, 2)
  qUpdate(__Q, 1, 1, 1, 2, true, 0.5, 0.9)
  expect(__Q[1][1], "Q[1][right]").toBeCloseTo(0.5)
})
test("a normal update bootstraps from the next state", () => {
  const __Q = [[0, 0], [0, 0], [0.8, 0.4]]
  qUpdate(__Q, 1, 1, 0, 2, false, 1, 0.9)
  expect(__Q[1][1], "Q[1][right]").toBeCloseTo(0.72)
})
test("after training, every square prefers going right", () => {
  random.seed(4)
  const __Q = train(300)
  expect([1, 2, 3, 4, 5].every(s => __Q[s][1] > __Q[s][0]), "right beats left everywhere").toBeTruthy()
})
test("the square next to the goal values 'right' at about 1", () => {
  random.seed(4)
  expect(train(300)[5][1], "Q[5][right]").toBeCloseTo(1, 0.05)
})
`,
}

// ─── 10.3 Diffusion ───────────────────────────────────────────────────────────

export const diffusion: ExerciseDef = {
  starter: `// The forward process of a diffusion model.
// 1. alphaBars(betas): ᾱ_t = (1 − β₁)(1 − β₂)…(1 − β_t)  — the running product.
function alphaBars(betas: number[]): number[] {
  return betas
}

// 2. noisy(x0, eps, abar): jump straight to step t:
//    x_t = √ᾱ · x₀ + √(1 − ᾱ) · ε
function noisy(x0: number[], eps: number[], abar: number): number[] {
  return x0
}

const betas = linspace(0.0001, 0.2, 50)
const ab = alphaBars(betas)
console.log("signal left at t = 1, 25, 50:", [ab[0], ab[24], ab[49]].map(v => round(Math.sqrt(v), 3)))

// Noise a little spiral and watch it dissolve:
const pts = data.spiral(150, 1, 0.02).X
for (const t of [0, 10, 25, 49]) {
  plot.scatter(pts.map(p => noisy(p, [random.normal(), random.normal()], ab[t])), { title: \`t = \${t + 1}\` })
}
`,
  solution: `function alphaBars(betas: number[]): number[] {
  const out: number[] = []
  let prod = 1
  for (const b of betas) {
    prod *= 1 - b
    out.push(prod)
  }
  return out
}

function noisy(x0: number[], eps: number[], abar: number): number[] {
  return x0.map((x, i) => Math.sqrt(abar) * x + Math.sqrt(1 - abar) * eps[i])
}

const betas = linspace(0.0001, 0.2, 50)
const ab = alphaBars(betas)
console.log("signal left at t = 1, 25, 50:", [ab[0], ab[24], ab[49]].map(v => round(Math.sqrt(v), 3)))

const pts = data.spiral(150, 1, 0.02).X
for (const t of [0, 10, 25, 49]) {
  plot.scatter(pts.map(p => noisy(p, [random.normal(), random.normal()], ab[t])), { title: \`t = \${t + 1}\` })
}
`,
  tests: `
test("ᾱ is the running product of (1 − β)", () => expect(alphaBars([0.1, 0.2, 0.5]), "alphaBars([0.1, 0.2, 0.5])").toBeCloseTo([0.9, 0.72, 0.36]))
test("ᾱ only goes down", () => {
  const a = alphaBars(linspace(0.01, 0.2, 20))
  expect(a.every((v, i) => i === 0 || v < a[i - 1]), "decreasing").toBeTruthy()
})
test("with ᾱ = 1 the data is untouched", () => expect(noisy([2, -1], [5, 5], 1), "noisy(x, ε, 1)").toBeCloseTo([2, -1]))
test("with ᾱ = 0 only noise remains", () => expect(noisy([2, -1], [0.3, 0.7], 0), "noisy(x, ε, 0)").toBeCloseTo([0.3, 0.7]))
test("in between, it's the right blend", () => expect(noisy([1, 0], [0, 1], 0.36), "noisy([1,0], [0,1], 0.36)").toBeCloseTo([0.6, 0.8]))
`,
}

// ─── 10.4 Efficiency ──────────────────────────────────────────────────────────

export const quantize: ExerciseDef = {
  starter: `// Symmetric int8 quantization.
//   scale = max(|w|) / 127
//   q = round(w / scale), clamped to [−127, 127]      (stored as small integers)
//   dequantized w ≈ q · scale
function quantize(ws: number[]): { q: number[]; scale: number } {
  return { q: ws, scale: 1 }
}
function dequantize(q: number[], scale: number): number[] {
  return q
}

const weights = range(1000).map(() => random.normal(0, 0.5))
const { q, scale } = quantize(weights)
const back = dequantize(q, scale)
const maxErr = Math.max(...weights.map((w, i) => Math.abs(w - back[i])))
console.log("scale:", scale, " max error:", maxErr, " sample ints:", q.slice(0, 8))
`,
  solution: `function quantize(ws: number[]): { q: number[]; scale: number } {
  const maxAbs = Math.max(...ws.map(Math.abs))
  const scale = maxAbs / 127
  const q = ws.map(w => Math.max(-127, Math.min(127, Math.round(w / scale))))
  return { q, scale }
}
function dequantize(q: number[], scale: number): number[] {
  return q.map(v => v * scale)
}

const weights = range(1000).map(() => random.normal(0, 0.5))
const { q, scale } = quantize(weights)
const back = dequantize(q, scale)
const maxErr = Math.max(...weights.map((w, i) => Math.abs(w - back[i])))
console.log("scale:", scale, " max error:", maxErr, " sample ints:", q.slice(0, 8))
`,
  tests: `
const __w = [0.5, -1.27, 0.01, 1.0]
test("the scale maps the largest weight to ±127", () => expect(quantize(__w).scale, "scale").toBeCloseTo(1.27 / 127))
test("quantized values are whole numbers within ±127", () => expect(quantize(__w).q, "q").toEqual([50, -127, 1, 100]))
test("dequantize multiplies back by the scale", () => expect(dequantize([50, -127], 0.01), "dequantize").toBeCloseTo([0.5, -1.27]))
test("round-trip error is at most half a step", () => {
  const ws = range(500).map(() => random.normal(0, 1))
  const { q, scale } = quantize(ws)
  const back = dequantize(q, scale)
  expect(Math.max(...ws.map((w, i) => Math.abs(w - back[i]))), "max error").toBeLessThan(scale / 2 + 1e-12)
})
`,
}

export const kvCache: ExerciseDef = {
  starter: `// Generating n tokens one at a time. Each new token needs the keys & values
// of EVERY earlier token. Count how many key/value computations each strategy does.

// Without a cache: at step t (1, 2, …, n) recompute keys/values for all t tokens.
function workWithoutCache(n: number): number {
  return 0
}

// With a cache: compute each token's key/value once, store it, reuse it later.
function workWithCache(n: number): number {
  return 0
}

for (const n of [10, 100, 1000]) {
  console.log(n, "tokens →", workWithoutCache(n), "vs", workWithCache(n))
}
`,
  solution: `function workWithoutCache(n: number): number {
  let total = 0
  for (let t = 1; t <= n; t++) total += t
  return total
}

function workWithCache(n: number): number {
  return n
}

for (const n of [10, 100, 1000]) {
  console.log(n, "tokens →", workWithoutCache(n), "vs", workWithCache(n))
}
`,
  tests: `
test("without a cache, 3 tokens cost 1 + 2 + 3 = 6", () => expect(workWithoutCache(3), "workWithoutCache(3)").toBe(6))
test("without a cache, 1000 tokens cost 500500", () => expect(workWithoutCache(1000), "workWithoutCache(1000)").toBe(500500))
test("with a cache, each token is computed once", () => expect(workWithCache(1000), "workWithCache(1000)").toBe(1000))
`,
}

// ─── 10.5 Reasoning & agents ──────────────────────────────────────────────────

export const majority: ExerciseDef = {
  starter: `// Self-consistency: ask the model several times, take the most common answer.

// 1. majorityVote(answers): the answer that appears most often (ties: the first one to reach the top count).
function majorityVote(answers: string[]): string {
  return answers[0]
}

// A pretend model: right ("42") with probability p, otherwise one of several wrong answers.
function askModel(p: number): string {
  return random.random() < p ? "42" : random.choice(["41", "43", "24", "420"])
}

// 2. accuracy(p, k, trials): how often the majority vote of k answers is right.
function accuracy(p: number, k: number, trials: number): number {
  return 0
}

for (const k of [1, 3, 5, 11, 21]) console.log("k =", k, " accuracy:", accuracy(0.4, k, 2000))
`,
  solution: `function majorityVote(answers: string[]): string {
  const counts = new Map<string, number>()
  let best = answers[0]
  for (const a of answers) {
    const c = (counts.get(a) ?? 0) + 1
    counts.set(a, c)
    if (c > (counts.get(best) ?? 0)) best = a
  }
  return best
}

function askModel(p: number): string {
  return random.random() < p ? "42" : random.choice(["41", "43", "24", "420"])
}

function accuracy(p: number, k: number, trials: number): number {
  let right = 0
  for (let t = 0; t < trials; t++) {
    const answers = range(k).map(() => askModel(p))
    if (majorityVote(answers) === "42") right++
  }
  return right / trials
}

for (const k of [1, 3, 5, 11, 21]) console.log("k =", k, " accuracy:", accuracy(0.4, k, 2000))
`,
  tests: `
test("majorityVote picks the most common answer", () => expect(majorityVote(["a", "b", "a", "c"]), "majorityVote").toBe("a"))
test("majorityVote works when the winner comes last", () => expect(majorityVote(["x", "y", "y"]), "majorityVote").toBe("y"))
test("one sample: accuracy ≈ p", () => { random.seed(2); expect(accuracy(0.4, 1, 4000), "accuracy(0.4, 1)").toBeCloseTo(0.4, 0.03) })
test("voting over 21 samples beats a single sample by a lot", () => { random.seed(3); expect(accuracy(0.4, 21, 2000), "accuracy(0.4, 21)").toBeGreaterThan(0.75) })
`,
}

export const agentLoop: ExerciseDef = {
  starter: `// The skeleton of an AI agent: think → act (use a tool) → observe → repeat.
// Here the "model" is a scripted stand-in so you can see the loop itself.
type Action = { tool: "calculator"; input: string } | { tool: "finish"; input: string }

const tools = {
  calculator: (expr: string) => String(Function(\`"use strict"; return (\${expr.replace(/[^0-9+*/().\\s-]/g, "")})\`)()),
}

function fakeModel(history: string[]): Action {
  // A real LLM would read the history and decide. This one follows a script.
  if (!history.some(h => h.startsWith("observation"))) return { tool: "calculator", input: "23 * 17 + 4" }
  const last = history[history.length - 1].split(": ")[1]
  return { tool: "finish", input: \`The answer is \${last}.\` }
}

const history = ["task: what is 23 × 17 + 4?"]
for (let turn = 0; turn < 5; turn++) {
  const action = fakeModel(history)
  console.log("action:", action)
  if (action.tool === "finish") {
    console.log("final:", action.input)
    break
  }
  const result = tools[action.tool](action.input)
  history.push(\`observation: \${result}\`)
  console.log("observation:", result)
}
`,
}
